import { redirectToLogin } from '@/lib/authUtils';

/** Carries the HTTP status so cold-start retry logic can tell 5xx (retryable)
 * from 4xx (not). Thrown by all the fetch helpers so every query's failure is
 * classifiable. */
export class ApiError extends Error {
  status: number;
  body: string;
  constructor(status: number, body: string) {
    super(`${status}: ${body}`);
    this.name = 'ApiError';
    this.status = status;
    this.body = body;
  }
}

/**
 * Get CSRF token from cookie
 */
export function getCsrfToken(): string | null {
  const cookies = document.cookie.split(';');
  for (const cookie of cookies) {
    const [name, value] = cookie.trim().split('=');
    if (name === 'csrf-token') {
      return decodeURIComponent(value);
    }
  }
  return null;
}

export async function authedFetch(
  input: RequestInfo | URL,
  init: RequestInit = {}
): Promise<Response> {
  const opts: RequestInit = {
    credentials: 'include',
    ...init,
    headers: {
      ...init.headers,
    },
  };

  // Add CSRF token for non-GET requests
  const method = (init.method || 'GET').toUpperCase();
  if (method !== 'GET' && method !== 'HEAD' && method !== 'OPTIONS') {
    const csrfToken = getCsrfToken();
    if (csrfToken) {
      (opts.headers as Record<string, string>)['X-CSRF-Token'] = csrfToken;
    }
  }

  try {
    const res = await fetch(input, opts);
    if (res.status === 401) {
      // Centralized unauthorized handling for non-React Query calls
      redirectToLogin(500);
      const text = (await res.text()) || res.statusText || 'Unauthorized';
      throw new ApiError(401, text);
    }
    return res;
  } catch (err) {
    // Network errors or thrown 401s
    throw err instanceof Error ? err : new Error(String(err));
  }
}

export async function authedJson<T>(input: RequestInfo | URL, init: RequestInit = {}): Promise<T> {
  const res = await authedFetch(input, init);
  if (!res.ok) {
    const text = (await res.text()) || res.statusText;
    throw new ApiError(res.status, text);
  }
  return (await res.json()) as T;
}
